import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import type { Contact, ContactActivityLog, ContactConsent, ContactProfile, UpdateContactPayload, CreateContactPayload, IdentifierType } from "@/types/contacts";
import { contactService } from "@/lib/api-services/contact-service";
import type { GetContactsParams } from "@/lib/api-services/contact-service";

interface ContactState {
  contacts: Contact[];
  currentContact: ContactProfile | null;
  totalCount: number;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  consents: ContactConsent[];
  isLoadingConsents: boolean;
  consentsContactId: string | null;
  activityLog: ContactActivityLog[];
  isLoadingActivityLog: boolean;
  activityLogContactId: string | null;

  fetchContacts: (accountId: string, params?: GetContactsParams) => Promise<void>;
  fetchContact: (accountId: string, contactId: string) => Promise<void>;
  createContact: (accountId: string, data: CreateContactPayload) => Promise<Contact>;
  updateContact: (accountId: string, contactId: string, data: UpdateContactPayload) => Promise<Contact>;
  deleteContact: (accountId: string, contactId: string) => Promise<void>;
  anonymizeContact: (accountId: string, contactId: string) => Promise<void>;
  addIdentifier: (
    accountId: string,
    contactId: string,
    data: { identifierType: IdentifierType; value: string; isPrimary?: boolean; label?: string },
  ) => Promise<void>;
  removeIdentifier: (accountId: string, contactId: string, type: IdentifierType, value: string) => Promise<void>;
  setAttributes: (accountId: string, contactId: string, attrs: Record<string, string>) => Promise<void>;
  fetchConsents: (accountId: string, contactId: string) => Promise<void>;
  fetchActivityLog: (accountId: string, contactId: string) => Promise<void>;
  reset: () => void;
}

export const useContactStore = create<ContactState>()(
  immer((set) => ({
    contacts: [],
    currentContact: null,
    totalCount: 0,
    isLoading: false,
    isSaving: false,
    error: null,
    consents: [],
    isLoadingConsents: false,
    consentsContactId: null,
    activityLog: [],
    isLoadingActivityLog: false,
    activityLogContactId: null,

    fetchContacts: async (accountId, params) => {
      set((state) => { state.isLoading = true; state.error = null; });
      try {
        const result = await contactService.getContacts(accountId, params);
        set((state) => {
          state.contacts = result.items;
          state.totalCount = result.totalCount;
          state.isLoading = false;
        });
      } catch (err) {
        set((state) => {
          state.isLoading = false;
          state.error = err instanceof Error ? err.message : "Failed to load contacts";
        });
      }
    },

    fetchContact: async (accountId, contactId) => {
      set((state) => { state.isLoading = true; state.error = null; });
      try {
        const contact = await contactService.getContact(accountId, contactId);
        set((state) => { state.currentContact = contact; state.isLoading = false; });
      } catch (err) {
        set((state) => {
          state.isLoading = false;
          state.error = err instanceof Error ? err.message : "Failed to load contact";
        });
      }
    },

    createContact: async (accountId, data) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        const contact = await contactService.createContact(accountId, data);
        set((state) => { state.contacts.unshift(contact); state.totalCount += 1; state.isSaving = false; });
        return contact;
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to create contact";
        });
        throw err;
      }
    },

    updateContact: async (accountId, contactId, data) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        const updated = await contactService.updateContact(accountId, contactId, data);
        set((state) => {
          const idx = state.contacts.findIndex((c) => c.id === contactId);
          if (idx !== -1) state.contacts[idx] = updated;
          if (state.currentContact?.id === contactId) {
            Object.assign(state.currentContact, updated);
          }
          state.isSaving = false;
        });
        return updated;
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to update contact";
        });
        throw err;
      }
    },

    deleteContact: async (accountId, contactId) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        await contactService.deleteContact(accountId, contactId);
        set((state) => {
          state.contacts = state.contacts.filter((c) => c.id !== contactId);
          state.totalCount = Math.max(0, state.totalCount - 1);
          if (state.currentContact?.id === contactId) state.currentContact = null;
          state.isSaving = false;
        });
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to delete contact";
        });
        throw err;
      }
    },

    anonymizeContact: async (accountId, contactId) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        await contactService.anonymizeContact(accountId, contactId);
        set((state) => {
          state.contacts = state.contacts.filter((c) => c.id !== contactId);
          state.totalCount = Math.max(0, state.totalCount - 1);
          if (state.currentContact?.id === contactId) state.currentContact = null;
          state.isSaving = false;
        });
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to anonymize contact";
        });
        throw err;
      }
    },

    addIdentifier: async (accountId, contactId, data) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        await contactService.addIdentifier(accountId, contactId, data);
        const refreshed = await contactService.getContact(accountId, contactId);
        set((state) => {
          if (state.currentContact?.id === contactId) state.currentContact = refreshed;
          state.isSaving = false;
        });
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to add identifier";
        });
        throw err;
      }
    },

    removeIdentifier: async (accountId, contactId, type, value) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        await contactService.removeIdentifier(accountId, contactId, type, value);
        const refreshed = await contactService.getContact(accountId, contactId);
        set((state) => {
          if (state.currentContact?.id === contactId) state.currentContact = refreshed;
          state.isSaving = false;
        });
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to remove identifier";
        });
        throw err;
      }
    },

    setAttributes: async (accountId, contactId, attrs) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        await contactService.setAttributes(accountId, contactId, attrs);
        set((state) => {
          if (state.currentContact?.id === contactId) {
            state.currentContact.attributeDictionary = { ...state.currentContact.attributeDictionary, ...attrs };
          }
          state.isSaving = false;
        });
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to save attributes";
        });
        throw err;
      }
    },

    fetchConsents: async (accountId, contactId) => {
      set((state) => { state.isLoadingConsents = true; });
      try {
        const consents = await contactService.getConsents(accountId, contactId);
        set((state) => {
          state.consents = consents;
          state.consentsContactId = contactId;
          state.isLoadingConsents = false;
        });
      } catch {
        set((state) => { state.isLoadingConsents = false; });
      }
    },

    fetchActivityLog: async (accountId, contactId) => {
      set((state) => { state.isLoadingActivityLog = true; });
      try {
        const log = await contactService.getActivityLog(accountId, contactId);
        set((state) => {
          state.activityLog = log;
          state.activityLogContactId = contactId;
          state.isLoadingActivityLog = false;
        });
      } catch {
        set((state) => { state.isLoadingActivityLog = false; });
      }
    },

    reset: () => {
      set((state) => {
        state.contacts = [];
        state.currentContact = null;
        state.totalCount = 0;
        state.isLoading = false;
        state.isSaving = false;
        state.error = null;
        state.consents = [];
        state.isLoadingConsents = false;
        state.consentsContactId = null;
        state.activityLog = [];
        state.isLoadingActivityLog = false;
        state.activityLogContactId = null;
      });
    },
  })),
);
